import { Helmet } from 'react-helmet-async';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://optimistic-news.com/privacy" />
        <title>Privacy Policy - Optimistic News</title>
        <meta
          name="description"
          content="Privacy Policy for Optimistic News — how we collect, process, and protect your personal data under the GDPR."
        />
      </Helmet>
      <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto prose dark:prose-invert">
        <h1>Privacy Policy</h1>
        <p>Last updated: March 27, 2026</p>

        <h2>1. Controller Identity and Contact</h2>
        <p>
          The controller responsible for processing your personal data within
          the meaning of Art. 4(7) GDPR is:
        </p>
        <p>
          Hannah
          <br />
          Am Botten, 53179 Bonn
          <br />
          E-Mail:{' '}
          <a href="mailto:privacy@optimistic-news.com">
            privacy@optimistic-news.com
          </a>
        </p>

        <h2>2. Processing Activities, Purposes, and Legal Bases</h2>
        <p>
          We process personal data only where a legal basis under Art. 6(1) GDPR
          exists. The table below sets out the main processing activities.
        </p>

        <h3>2.1 Account Registration and Authentication</h3>
        <p>
          <strong>Data:</strong> Email address, password (hashed), account
          timestamps.
        </p>
        <p>
          <strong>Purpose:</strong> Creating and maintaining your user account,
          authenticating you on each visit, and sending account-related
          transactional emails (email verification, password reset).
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(b) GDPR — performance of the
          contract with you (Terms of Service).
        </p>

        <h3>2.2 Paid Subscriptions</h3>
        <p>
          <strong>Data:</strong> Email address, payment method details (handled
          directly by Stripe), subscription status, billing history.
        </p>
        <p>
          <strong>Purpose:</strong> Processing payments, managing subscription
          entitlements, and complying with tax and bookkeeping obligations.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(b) GDPR — performance of
          contract; Art. 6(1)(c) GDPR — legal obligation (tax / accounting law).
        </p>

        <h3>2.3 Newsletter and Marketing Emails</h3>
        <p>
          <strong>Data:</strong> Email address, opt-in timestamp, preferences
          (topics, frequency).
        </p>
        <p>
          <strong>Purpose:</strong> Sending the weekly positive-news digest and
          service announcements to subscribers who have given explicit consent.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(a) GDPR — consent (opt-in).
          You may withdraw consent at any time by clicking the unsubscribe link
          in any newsletter or by contacting us at{' '}
          <a href="mailto:privacy@optimistic-news.com">
            privacy@optimistic-news.com
          </a>
          .
        </p>

        <h3>2.4 Error Monitoring and Application Stability</h3>
        <p>
          <strong>Data:</strong> IP address (truncated), browser/OS type, error
          stack traces, page URL at the time of error, session replay data (on
          error events).
        </p>
        <p>
          <strong>Purpose:</strong> Detecting and diagnosing software errors to
          maintain a stable and secure service.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(f) GDPR — legitimate interest
          (ensuring service reliability and security).
        </p>

        <h3>2.5 Analytics</h3>
        <p>
          <strong>Data:</strong> Pseudonymous usage events (page views,
          interactions), IP address (anonymised).
        </p>
        <p>
          <strong>Purpose:</strong> Understanding how the service is used in
          aggregate to improve features and content.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(a) GDPR — consent, obtained
          via the cookie-consent banner before any analytics scripts are loaded.
          You may withdraw consent at any time through the cookie settings link
          in the footer.
        </p>

        <h3>2.6 Log Data and Security</h3>
        <p>
          <strong>Data:</strong> Server access logs (IP address, request path,
          timestamp, HTTP status code).
        </p>
        <p>
          <strong>Purpose:</strong> Detecting abuse, DDoS attacks, and
          unauthorised access; fulfilling statutory security obligations.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6(1)(f) GDPR — legitimate interest
          (IT security and fraud prevention).
        </p>

        <h2>3. Processors and International Transfers</h2>
        <p>
          We use the following sub-processors acting on our behalf pursuant to
          Art. 28 GDPR. Where data is transferred outside the EU/EEA, we rely on
          the transfer mechanisms indicated.
        </p>

        <table>
          <thead>
            <tr>
              <th>Processor</th>
              <th>Purpose</th>
              <th>Data location / Transfer basis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>Sentry</strong> (Functional Software, Inc. / Sentry EU
                endpoint)
              </td>
              <td>Error monitoring and session replay on error</td>
              <td>
                EU region (Frankfurt). No transfer outside EEA for data sent to
                the EU endpoint.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Stripe</strong> (Stripe Payments Europe, Ltd.)
              </td>
              <td>Payment processing and subscription management</td>
              <td>
                EU and US. Transfer to US covered by Stripe's Standard
                Contractual Clauses (SCCs) and EU–US Data Privacy Framework
                certification.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Mailgun</strong> (Sinch Email Inc.)
              </td>
              <td>Transactional and newsletter email delivery</td>
              <td>
                EU region available; we use the EU data region. SCCs in place
                for any residual transfers.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Google Analytics</strong> (Google Ireland Ltd.)
              </td>
              <td>Aggregate website analytics (consent-gated)</td>
              <td>
                EU and US. Transfer to US covered by Google's SCCs and EU–US
                Data Privacy Framework. Only activated after user consent.
              </td>
            </tr>
            <tr>
              <td>
                <strong>Fly.io</strong> (Fly.io, Inc.)
              </td>
              <td>Application and database hosting</td>
              <td>
                EU region (Amsterdam). SCCs in place for data processed by
                Fly.io infrastructure.
              </td>
            </tr>
          </tbody>
        </table>

        <h2>4. Data Retention</h2>
        <p>
          We retain personal data only as long as necessary for the purpose for
          which it was collected, or as required by law.
        </p>
        <ul>
          <li>
            {/* DL-003: No grace period — deletion is immediate and permanent. */}
            <strong>Account data:</strong> Account data is deleted immediately
            and permanently upon your request. This deletion cannot be undone.
          </li>
          <li>
            <strong>Billing records:</strong> 10 years from the end of the
            relevant fiscal year, as required by German commercial and tax law
            (§ 257 HGB, § 147 AO).
          </li>
          <li>
            <strong>Newsletter consent records:</strong> For as long as the
            subscription is active plus 3 years after unsubscription (for
            compliance documentation).
          </li>
          <li>
            <strong>Error and log data:</strong> 90 days rolling retention.
          </li>
          <li>
            <strong>Analytics data:</strong> Aggregated/anonymised data retained
            indefinitely; raw pseudonymous event data deleted after 26 months.
          </li>
        </ul>

        <h2>5. Cookies and Consent</h2>
        <p>
          We use strictly necessary cookies required for authentication and
          session management. These do not require consent. Optional cookies
          (analytics, marketing) are only set after you provide consent via our
          cookie-consent banner. You can review and change your cookie
          preferences at any time using the cookie-settings link in the footer.
        </p>
        <ul>
          <li>
            <strong>Strictly necessary cookies:</strong> Session tokens,
            CSRF-protection tokens — legal basis Art. 6(1)(b) and Art. 6(1)(f)
            GDPR.
          </li>
          <li>
            <strong>Analytics cookies (Google Analytics):</strong> Only set
            after consent — legal basis Art. 6(1)(a) GDPR.
          </li>
        </ul>

        <h2>6. Your Rights Under the GDPR (Art. 15–21)</h2>
        <p>
          As a data subject you have the following rights. To exercise any of
          them, contact us at{' '}
          <a href="mailto:privacy@optimistic-news.com">
            privacy@optimistic-news.com
          </a>
          .
        </p>
        <ul>
          <li>
            <strong>Art. 15 — Right of access:</strong> You may request a copy
            of all personal data we hold about you.
          </li>
          <li>
            <strong>Art. 16 — Right to rectification:</strong> You may ask us to
            correct inaccurate or incomplete data.
          </li>
          <li>
            <strong>
              Art. 17 — Right to erasure ("right to be forgotten"):
            </strong>{' '}
            You may request deletion of your personal data where no overriding
            legal basis applies.
          </li>
          <li>
            <strong>Art. 18 — Right to restriction of processing:</strong> You
            may ask us to restrict processing in certain circumstances (e.g.,
            while a rectification request is pending).
          </li>
          <li>
            <strong>Art. 20 — Right to data portability:</strong> You may
            request a machine-readable export of data you have provided to us.
            You can generate this export directly from your account settings
            page.
          </li>
          <li>
            <strong>Art. 21 — Right to object:</strong> You may object to
            processing based on legitimate interest (Art. 6(1)(f)) at any time.
            We will stop processing unless we can demonstrate compelling
            legitimate grounds that override your interests.
          </li>
          <li>
            <strong>Right to withdraw consent (Art. 7(3)):</strong> Where
            processing is based on consent, you may withdraw it at any time
            without affecting the lawfulness of prior processing.
          </li>
        </ul>
        <p>
          We will respond to your request without undue delay and no later than
          one month after receipt (Art. 12(3) GDPR), extendable by a further two
          months for complex requests.
        </p>

        <h2>7. Right to Lodge a Complaint</h2>
        <p>
          You have the right to lodge a complaint with a supervisory authority
          if you believe we have processed your personal data in violation of
          the GDPR. The competent supervisory authority for our establishment
          is:
        </p>
        <p>
          <strong>
            Landesbeauftragte für Datenschutz und Informationsfreiheit
            Nordrhein-Westfalen (LDI NRW)
          </strong>
          <br />
          Postfach 20 04 44
          <br />
          40102 Düsseldorf
          <br />
          Germany
          <br />
          Phone: +49 (0) 211 384 24-0
          <br />
          Website:{' '}
          <a
            href="https://www.ldi.nrw.de"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.ldi.nrw.de
          </a>
        </p>
        <p>
          You also have the right to lodge a complaint with the supervisory
          authority in the EU member state of your habitual residence, place of
          work, or the place of the alleged infringement.
        </p>

        <h2>8. Security</h2>
        <p>
          We implement appropriate technical and organisational measures to
          protect your personal data against accidental or unlawful destruction,
          loss, alteration, unauthorised disclosure, or access. These measures
          include encrypted data transmission (TLS), hashed password storage,
          access controls, and regular security monitoring. No method of
          transmission over the Internet or electronic storage is 100% secure;
          we cannot guarantee absolute security.
        </p>

        <h2>9. Links to Other Websites</h2>
        <p>
          Our service contains links to third-party websites we do not operate.
          We have no control over and accept no responsibility for the privacy
          practices or content of those sites. We encourage you to review the
          privacy policy of every site you visit.
        </p>

        <h2>10. Children</h2>
        <p>
          This service is not directed at persons under the age of 16. We do not
          knowingly collect personal data from children. If you believe a child
          has provided us with personal data, please contact us and we will
          delete it.
        </p>

        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy to reflect changes in our practices
          or applicable law. We will notify you of material changes by email
          and/or by a prominent notice on the service before the change takes
          effect, and we will update the "Last updated" date at the top of this
          page.
        </p>

        <h2>12. Contact</h2>
        <p>
          For any questions or requests relating to this Privacy Policy or your
          personal data, please contact:
        </p>
        <p>
          Hannah
          <br />
          <a href="mailto:privacy@optimistic-news.com">
            privacy@optimistic-news.com
          </a>
        </p>
      </div>
    </>
  );
}
